import { supabase } from './supabase'

// Payroll data access. GM/admin only — every table's RLS enforces that,
// so these functions simply return nothing for anyone else rather than
// relying on the screen to hide them.

export async function loadEmployees(branchId, { includeLeavers = false } = {}) {
  let q = supabase.from('payroll_employee')
    .select('*').eq('branch_id', branchId)
  if (!includeLeavers) q = q.is('ended_on', null)
  const { data, error } = await q.order('tier').order('full_name')
  if (error) throw error
  return data || []
}

export async function saveEmployee(emp) {
  const row = { ...emp }
  delete row.created_at
  const { data, error } = row.id
    ? await supabase.from('payroll_employee').update(row).eq('id', row.id).select().single()
    : await supabase.from('payroll_employee').insert(row).select().single()
  if (error) throw error
  return data
}

// Leaving is a DATE, never a delete: past months must stay intact.
export async function endEmployment(id, endedOn) {
  const { error } = await supabase.from('payroll_employee')
    .update({ ended_on: endedOn }).eq('id', id)
  if (error) throw error
}

export async function loadPeriod(branchId, year, month) {
  const { data, error } = await supabase.from('payroll_period')
    .select('*').eq('branch_id', branchId).eq('year', year).eq('month', month).maybeSingle()
  if (error) throw error
  return data
}

// Opening a month creates it and brings in everyone employed that month,
// COPYING their salary and bank details onto each line.
export async function openPeriod(branchId, year, month, workingDays = 28) {
  let period = await loadPeriod(branchId, year, month)
  if (!period) {
    const { data, error } = await supabase.from('payroll_period')
      .insert({ branch_id: branchId, year, month, working_days: workingDays })
      .select().single()
    if (error) throw error
    period = data
  }
  if (period.status === 'final') return period

  const monthEnd = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`
  const emps = await loadEmployees(branchId, { includeLeavers: true })
  const eligible = emps.filter(e =>
    (!e.started_on || e.started_on <= monthEnd) && (!e.ended_on || e.ended_on >= monthStart))

  const { data: existing } = await supabase.from('payroll_line')
    .select('employee_id').eq('period_id', period.id)
  const have = new Set((existing || []).map(l => l.employee_id))
  const rows = eligible.filter(e => !have.has(e.id)).map(e => ({
    period_id: period.id, employee_id: e.id,
    full_name: e.full_name, role_title: e.role_title, tier: e.tier,
    monthly_salary: e.monthly_salary, bank_name: e.bank_name, bank_account: e.bank_account,
    days_worked: period.working_days,
  }))
  if (rows.length) {
    const { error } = await supabase.from('payroll_line').insert(rows)
    if (error) throw error
  }
  return period
}

export async function loadLines(periodId) {
  const { data, error } = await supabase.from('payroll_line')
    .select('*, payroll_deduction(*), payroll_addition(*)')
    .eq('period_id', periodId)
  if (error) throw error
  return (data || []).sort((a, b) =>
    (a.tier === b.tier ? 0 : a.tier === 'management' ? -1 : 1) ||
    String(a.full_name).localeCompare(String(b.full_name)))
}

export async function updateLine(id, patch) {
  const { error } = await supabase.from('payroll_line').update(patch).eq('id', id)
  if (error) throw error
}

export async function addDeduction(lineId, d) {
  const { error } = await supabase.from('payroll_deduction')
    .insert({ line_id: lineId, ...d })
  if (error) throw error
}

export async function removeDeduction(id) {
  const { error } = await supabase.from('payroll_deduction').delete().eq('id', id)
  if (error) throw error
}

// What each linked employee currently owes at the departments, so the
// month's deductions can be PROPOSED rather than typed. Nothing is
// written here — the GM reviews and edits before anything is saved.
// Proposes each employee's credit TAKEN DURING THIS MONTH, not their
// whole running balance. Was pulling v_customer_balances_by_staff —
// everything ever owed — so opening a fresh month could deduct arrears
// from months already settled or already deducted elsewhere.
//
// Uses customer_credit_for_month (294), the same figures the Credit
// page's monthly view shows, so what gets proposed here always matches
// what the GM can see there.
//
// location_id is not in that function's output (it sums across
// departments for the month), so the single deduction line is tagged
// 'month' rather than a department — itemised by month instead of by
// where it was taken.
export async function proposeCreditDeductions(branchId, lines, year, month) {
  const ids = lines.map(l => l.customer_id).filter(Boolean)
  if (!ids.length || !year || !month) return {}
  // UNSETTLED credit taken in the month, per department (migration 310).
  //
  // Two earlier versions were wrong:
  //   - the first pulled the whole running balance, sweeping in arrears
  //   - the second pulled credit TAKEN in the month and never looked at
  //     repayments, so it deducted money people had already paid back
  //     (GM took 49,550, only 1,850 still owed). It also summed across
  //     departments and wrote none, so on finalising the repayment would
  //     not have reduced any department's debt — credit is tracked per
  //     department.
  // The database works out, per customer and department, what is still
  // unsettled first-in-first-out, counting every repayment to date.
  const { data, error } = await supabase.rpc('customer_unsettled_credit_for_month', {
    p_branch: branchId, p_year: year, p_month: month,
  })
  if (error) throw error
  const out = {}
  for (const r of (data || [])) {
    if (!ids.includes(r.customer_id)) continue
    const amount = Number(r.unsettled || 0)
    if (amount <= 0.009) continue            // settled — nothing to deduct
    ;(out[r.customer_id] || (out[r.customer_id] = [])).push({
      location_id: r.location_id, amount,
      note: `Unsettled credit from ${String(month).padStart(2, '0')}/${year}`,
    })
  }
  return out
}

// Finalising freezes the month AND clears the credit it deducted: a
// deduction settles that debt, so the customer balance must come down or
// the same money sits owing in two places.
//
// Written with method 'payroll' (migration 276) so it NEVER counts as
// cash taken — otherwise the day's "Debt recovered" and "Total income"
// would include wages deducted, and whoever cashed up would be chasing
// money that never arrived.
// Finalising is the one moment that commits everything the month
// decided. Three things were only ever READ elsewhere in this file and
// never WRITTEN by anything — found by checking, not assumed:
//   1. a credit deduction settling department debt     (this was here)
//   2. money typed as "held as savings" on a line NEVER reached
//      payroll_savings_entry — the ledger savingsBalances() reads from.
//      The deduction correctly reduced pay; nothing ever recorded that
//      the money was being HELD. "Saved NX" on the staff list could
//      never move, whatever was deducted.
//   3. the pot payout: savePayout() existed but nothing called it, and
//      payroll_pot_member.taken_period_id — the field that marks whose
//      turn is done — was never set anywhere. The rotation could not
//      advance even if a payout was recorded by hand.
// All three now commit together here, or none do.
export async function finalisePeriod(periodId, staffId) {
  const { data: period } = await supabase.from('payroll_period')
    .select('id, branch_id, year, month, status').eq('id', periodId).single()
  if (period?.status === 'final') throw new Error('That month is already finalised.')

  const { data: lines } = await supabase.from('payroll_line')
    .select('id, employee_id, full_name, savings, payroll_deduction(source, amount, location_id, customer_id, note)')
    .eq('period_id', periodId)

  // Built in UTC. new Date(year, month, 0) is LOCAL midnight, and on a
  // Lagos phone converting that to UTC for printing gave 29 September,
  // so every credit repayment and savings deposit would have been dated
  // a day before the month ended.
  const lastDay = new Date(Date.UTC(period.year, period.month, 0)).toISOString().slice(0, 10)

  // Re-check that every credit deduction is STILL owed. A deduction is
  // worked out when credit is pulled, which can be days before the month
  // is finalised; someone who pays their debt in between would otherwise
  // be deducted from their pay for money they have already paid. Refuses
  // outright rather than quietly capping: the pay figures are already
  // set, and shaving the repayment would leave pay and the credit book
  // disagreeing about how much was settled.
  let balRows = []   // ledger rows (customer x department x staff) for the allocation below
  const custIds = [...new Set((lines || []).flatMap(l =>
    (l.payroll_deduction || []).filter(d => d.source === 'credit' && d.customer_id)
      .map(d => d.customer_id)))]
  if (custIds.length) {
    const { data: bal, error: bErr } = await supabase.from('v_customer_balances_by_staff')
      .select('customer_id, location_id, staff_id, balance, first_credit_date')
      .eq('branch_id', period.branch_id).in('customer_id', custIds)
    if (bErr) throw bErr
    balRows = (bal || []).map(b => ({ ...b, left: Number(b.balance || 0) }))
    const owed = {}   // `${customer}|${location}` -> still owed
    const owedTotal = {}
    for (const b of (bal || [])) {
      const k = `${b.customer_id}|${b.location_id}`
      owed[k] = (owed[k] || 0) + Number(b.balance || 0)
      owedTotal[b.customer_id] = (owedTotal[b.customer_id] || 0) + Number(b.balance || 0)
    }
    const stale = []
    for (const l of (lines || [])) {
      for (const d of (l.payroll_deduction || [])) {
        if (d.source !== 'credit' || !d.customer_id) continue
        const key = `${d.customer_id}|${d.location_id}`
        const left = d.location_id ? (owed[key] || 0) : (owedTotal[d.customer_id] || 0)
        if (Number(d.amount) - left > 0.009) {
          stale.push(`${l.full_name}: ${Number(d.amount).toLocaleString()} is set to be deducted but `
            + `${Math.max(0, left).toLocaleString()} is still owed`)
        }
        // Cumulative: a second deduction against the same debt must be
        // tested against what is left AFTER the first, not the full amount.
        if (d.location_id) owed[key] = left - Number(d.amount)
        owedTotal[d.customer_id] = (owedTotal[d.customer_id] || 0) - Number(d.amount)
      }
    }
    if (stale.length) {
      throw new Error('Not finalised — some credit has been paid since it was pulled. '
        + 'Remove the old credit deductions and pull again.\n' + stale.join('\n'))
    }
  }

  // 1. Credit deductions settle department debt. Each repayment is booked
  // against the staff member who GAVE the credit, in the department it was
  // given in — the Credit page works per department and per staff row, so a
  // repayment booked under whoever pressed Finalise would leave the debt
  // showing as owed on the original row. A deduction bigger than one row is
  // split across the rows that owe, oldest first.
  const repayments = []
  for (const l of (lines || [])) {
    for (const d of (l.payroll_deduction || [])) {
      if (d.source !== 'credit' || !d.customer_id) continue
      const note = String(d.note || '').startsWith('Linked account')
        ? `Recovered from ${l.full_name}'s salary (responsible for this account)`
        : `Deducted from ${l.full_name}'s salary`
      let remaining = Number(d.amount)
      const rows = balRows
        .filter(r => r.customer_id === d.customer_id && r.left > 0.009
          && (!d.location_id || r.location_id === d.location_id))
        .sort((a, b) => String(a.first_credit_date || '').localeCompare(String(b.first_credit_date || '')))
      for (const r of rows) {
        if (remaining <= 0.009) break
        const take = Math.min(remaining, r.left)
        repayments.push({
          branch_id: period.branch_id, customer_id: d.customer_id,
          location_id: r.location_id, amount: Math.round(take * 100) / 100, method: 'payroll',
          paid_on: lastDay, note, recorded_by: staffId, credit_staff_id: r.staff_id || staffId,
          payroll_period_id: periodId,
        })
        r.left -= take; remaining -= take
      }
      if (remaining > 0.009) {
        // Nothing left to attribute it to (should not happen after the check above).
        repayments.push({
          branch_id: period.branch_id, customer_id: d.customer_id,
          location_id: d.location_id, amount: Math.round(remaining * 100) / 100, method: 'payroll',
          paid_on: lastDay, note, recorded_by: staffId, credit_staff_id: staffId,
          payroll_period_id: periodId,
        })
      }
    }
  }
  if (repayments.length) {
    const { error } = await supabase.from('credit_repayments').insert(repayments)
    if (error) throw error
  }

  // 2. Money held as savings this month becomes a real deposit.
  const deposits = (lines || [])
    .filter(l => Number(l.savings) > 0.009)
    .map(l => ({
      employee_id: l.employee_id, period_id: periodId, entry_date: lastDay,
      kind: 'deposit', amount: Number(l.savings),
      note: `Held from ${l.full_name}'s ${period.year}-${String(period.month).padStart(2, '0')} salary`,
    }))
  if (deposits.length) {
    const { error } = await supabase.from('payroll_savings_entry').insert(deposits)
    if (error) throw error
  }

  // 3. A pot payout recorded for this period marks that member's turn
  // as taken, so payroll_pot_next() moves on to whoever is next.
  const { data: payouts } = await supabase.from('payroll_pot_payout')
    .select('employee_id').eq('period_id', periodId)
  if (payouts?.length) {
    const { error } = await supabase.from('payroll_pot_member')
      .update({ taken_period_id: periodId })
      .in('employee_id', payouts.map(p => p.employee_id))
      .is('taken_period_id', null)
    if (error) throw error
  }

  const { error: e2 } = await supabase.from('payroll_period')
    .update({ status: 'final', finalised_by: staffId, finalised_at: new Date().toISOString() })
    .eq('id', periodId)
  if (e2) throw e2
  return { repayments: repayments.length, deposits: deposits.length, payouts: payouts?.length || 0 }
}

export async function reopenPeriod(periodId) {
  // Undoes what finalising wrote (repayments, savings deposits, pot turns)
  // so the next finalise cannot double them (migration 372).
  const { error } = await supabase.rpc('reopen_payroll_period', { p_period: periodId })
  if (error) throw error
}

// ---- the management pot ----
export async function loadPotNext(branchId) {
  const { data, error } = await supabase.rpc('payroll_pot_next', { p_branch: branchId })
  if (error) return null
  return (data && data[0]) || null
}

export async function loadPayouts(periodId) {
  const { data, error } = await supabase.from('payroll_pot_payout')
    .select('*').eq('period_id', periodId)
  if (error) throw error
  return data || []
}

export async function savePayout(periodId, employeeId, amount, note) {
  const { error } = await supabase.from('payroll_pot_payout')
    .upsert({ period_id: periodId, employee_id: employeeId, amount, note },
            { onConflict: 'period_id,employee_id' })
  if (error) throw error
}

// ---- individual savings ----
export async function loadSavings(employeeId) {
  const { data, error } = await supabase.from('payroll_savings_entry')
    .select('*').eq('employee_id', employeeId).order('entry_date')
  if (error) throw error
  return data || []
}

export async function savingsBalances(branchId) {
  const { data: emps } = await supabase.from('payroll_employee')
    .select('id').eq('branch_id', branchId)
  const ids = (emps || []).map(e => e.id)
  if (!ids.length) return {}
  const { data } = await supabase.from('payroll_savings_entry')
    .select('employee_id, kind, amount').in('employee_id', ids)
  const out = {}
  for (const e of (data || [])) {
    out[e.employee_id] = (out[e.employee_id] || 0)
      + (e.kind === 'deposit' ? Number(e.amount) : -Number(e.amount))
  }
  return out
}

// ---- the arithmetic, in ONE place ----
// Nnewi's sheet is the consistent one, so its formula is the model.
// Awka's "Amount Receivable" column reads 0 for everyone — confirmed a
// broken formula, and deliberately not reproduced.
export function lineTotals(line, payoutAmount = 0, workingDays = 28) {
  const salary = Number(line.monthly_salary || 0)
  const days = Number(line.days_worked || 0)
  // Divide by the MONTH's working days, not a fixed 28: a 30-day month
  // paid on a 28-day divisor overpays everyone slightly.
  const wd = Number(workingDays) || 28
  const dailyRate = wd > 0 ? salary / wd : 0
  const earned = Math.round(dailyRate * days * 100) / 100
  const deductions = (line.payroll_deduction || []).reduce((t, d) => t + Number(d.amount), 0)
  // Typed additions PLUS itemised ones (the Staff of the Month prize lands
  // here, labelled, so it is visible rather than folded into one number).
  const additions = Number(line.additions || 0)
    + (line.payroll_addition || []).reduce((t, a) => t + Number(a.amount), 0)
  const contribution = Number(line.contribution || 0)   // paid INTO the pot
  const savings = Number(line.savings || 0)             // held back for themselves
  const pot = Number(payoutAmount || 0)                 // taken OUT of the pot

  // What actually reaches them this month, pot included.
  const net = earned - deductions + additions - contribution - savings + pot

  // Over-70,000 rule, on what they are ACTUALLY PAID — STRICTLY above,
  // so someone on exactly 70,000 is unaffected.
  //
  // Corrected from an earlier reading that used salary + payout. For the
  // GM in September that gave 350,000 and a gift of 280,000; the right
  // basis is 250,000 - 50,000 paid in + 250,000 received = 450,000, so
  // the gift is 380,000. September's sheet understated it by 100,000.
  const over = net > 70000
  const documented = over ? 70000 : net
  const gift = over ? net - 70000 : 0

  return { dailyRate, earned, deductions, additions, contribution, savings,
           pot, net, gross: net, documented, gift, overThreshold: over }
}

// Link an employee to their credit account AND rename that account to
// their AKA, in one database step (migration 285). One step matters: a
// rename that half-applied would leave the link pointing at an account
// still called "Chidera/staff".
//
// It refuses rather than guessing if the AKA is already another
// customer's name at that branch, or the account belongs to someone
// else.
export async function setEmployeeCreditAccount(employeeId, customerId, aka) {
  const { error } = await supabase.rpc('set_employee_credit_account', {
    p_employee: employeeId, p_customer: customerId || null, p_aka: aka || null,
  })
  if (error) throw error
}

// Customers this employee's credit could sit under. Staff credit is
// recorded as ordinary customer accounts ("Chioma (staff)"), so this is
// simply the branch's active customers, newest activity aside — the GM
// picks the right one once and it is stored.
export async function loadCustomersForLinking(branchId) {
  const { data, error } = await supabase.from('customers')
    .select('id, name')
    .eq('branch_id', branchId).eq('is_active', true)
    .order('name')
  if (error) throw error
  return data || []
}


// ---------- raises (migration 314) ----------

// Records the change, updates the employee, and updates every DRAFT month
// from the effective month onward. Refused if a finalised month falls on
// or after it, or if the month is in the future. `effectiveMonth` is
// 'YYYY-MM-01'. The database enforces all of this; the screen only
// reports what it says.
export async function giveRaise(employeeId, newSalary, effectiveMonth, reason) {
  const { data, error } = await supabase.rpc('give_raise', {
    p_employee: employeeId, p_new_salary: Number(newSalary),
    p_effective: effectiveMonth, p_reason: reason || null,
  })
  if (error) throw error
  return data   // { effective, open_months_updated }
}

export async function loadSalaryHistory(employeeId) {
  const { data, error } = await supabase.from('payroll_salary_change')
    .select('id, effective_from, old_salary, new_salary, reason, created_at')
    .eq('employee_id', employeeId)
    .order('effective_from', { ascending: false }).order('created_at', { ascending: false })
  if (error) throw error
  return data || []
}

// ---------- itemised additions, and the Staff of the Month prize ----------

export async function removeAddition(id) {
  const { error } = await supabase.from('payroll_addition').delete().eq('id', id)
  if (error) throw error
}

// Open (draft) months — the only ones a prize can be added to.
export async function loadDraftPeriods(branchId) {
  const { data, error } = await supabase.from('payroll_period')
    .select('id, year, month').eq('branch_id', branchId).eq('status', 'draft')
    .order('year').order('month')
  if (error) throw error
  return data || []
}

// Adds, MOVES or REMOVES the prize on the winner's payroll line, keyed on
// the award post: a same-day correction replaces it rather than doubling
// it. Passing no employee removes it.
export async function setAwardPayroll(awardId, employeeId, periodId, amount) {
  const { data, error } = await supabase.rpc('set_award_payroll', {
    p_award: awardId, p_employee: employeeId || null,
    p_period: periodId || null, p_amount: Number(amount) || 0,
  })
  if (error) throw error
  return data
}

// Where an award's prize currently sits, if anywhere — to pre-fill the form.
export async function loadAwardPayroll(awardId) {
  if (!awardId) return null
  const { data } = await supabase.from('payroll_addition')
    .select('amount, payroll_line(employee_id, period_id)')
    .eq('award_id', awardId).maybeSingle()
  if (!data?.payroll_line) return null
  return { employeeId: data.payroll_line.employee_id,
           periodId: data.payroll_line.period_id, amount: Number(data.amount) }
}


// ---------- accounts a staff member is responsible for (migration 348) ----------

// Who answers for each credit customer / guest account (GM / admin only; the
// database refuses everyone else). One responsible person per account.
export async function loadResponsibleLinks(branchId) {
  const { data, error } = await supabase.rpc('customer_responsibles', { p_branch: branchId })
  if (error) throw error
  return data || []
}

// employeeId null = unlink.
export async function setCustomerResponsible(customerId, employeeId) {
  const { error } = await supabase.rpc('set_customer_responsible', {
    p_customer: customerId, p_employee: employeeId || null,
  })
  if (error) throw error
}

// What each linked account still owes right now, to show beside the link.
export async function loadAccountBalances(branchId, customerIds) {
  if (!customerIds.length) return {}
  const { data, error } = await supabase.from('v_customer_balances_by_staff')
    .select('customer_id, balance').eq('branch_id', branchId).in('customer_id', customerIds)
  if (error) throw error
  const out = {}
  for (const b of (data || [])) out[b.customer_id] = (out[b.customer_id] || 0) + Number(b.balance || 0)
  return out
}

// The month's UNSETTLED credit on the accounts each employee is responsible for,
// the same figures and the same first-in-first-out rule as their own credit
// (customer_unsettled_credit_for_month). Returns { employeeId: [lines] }.
// Nothing is written here: the GM sees the lines and decides.
export async function proposeLinkedDeductions(branchId, links, year, month) {
  if (!links.length || !year || !month) return {}
  const byCustomer = Object.fromEntries(links.map(l => [l.customer_id, l]))
  const { data, error } = await supabase.rpc('customer_unsettled_credit_for_month', {
    p_branch: branchId, p_year: year, p_month: month,
  })
  if (error) throw error
  const out = {}
  for (const r of (data || [])) {
    const link = byCustomer[r.customer_id]
    const amount = Number(r.unsettled || 0)
    if (!link || amount <= 0.009) continue
    ;(out[link.employee_id] || (out[link.employee_id] = [])).push({
      customer_id: r.customer_id, location_id: r.location_id, amount,
      note: `Linked account ${link.customer_name}: unsettled credit from ${String(month).padStart(2, '0')}/${year}`,
    })
  }
  return out
}
